import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Mail, Stethoscope } from "lucide-react";
import { subscribeNewsletter } from "../../lib/api";
import BlogProductCard from "./BlogProductCard";

const Box = ({ title, children, testId }) => (
    <section className="border border-white/10 bg-bg1 p-5" data-testid={testId}>
        <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">{title}</div>
        {children}
    </section>
);

// Contextual sidebar. Empty blocks are never rendered.
export default function BlogSidebar({ post, popular = [], categories = [], onTrack, newsletter = true }) {
    const [email, setEmail] = React.useState("");
    const products = post.product_slugs || [];
    const categoryName = post.category?.name;

    const join = async (e) => {
        e.preventDefault();
        try {
            await subscribeNewsletter(email);
            toast.success("You're on the list");
            setEmail("");
        } catch { toast.error("Could not sign you up right now"); }
    };

    return (
        <div className="space-y-5" data-testid="blog-sidebar">
            <Box title="Consultation" testId="sidebar-consultation">
                <Stethoscope className="w-5 h-5 text-lime" />
                <h3 className="font-display text-xl uppercase tracking-tight mt-2">Need personalised guidance?</h3>
                <p className="text-sm text-zinc-400 mt-1.5">
                    {categoryName ? `Talk to a consultant about ${categoryName.toLowerCase()}.`
                        : "Talk to a ReHealthMe consultant about your concern."}
                </p>
                <Link to={post.consultation_area ? `/consultation?area=${post.consultation_area}` : "/consultation"}
                    onClick={() => onTrack?.("consultation_click", post.consultation_area || "sidebar")}
                    data-testid="sidebar-consultation-link"
                    className="mt-4 inline-flex items-center gap-2 border border-lime text-lime px-4 py-2 mono
                               text-[10px] tracking-[0.16em] font-bold hover:bg-lime hover:text-bg0 transition-colors">
                    BOOK A CONSULTATION <ArrowRight className="w-3.5 h-3.5" />
                </Link>
            </Box>

            {!!products.length && (
                <Box title="Shop ReHealthMe" testId="sidebar-products">
                    <div className="space-y-3">
                        {products.slice(0, 2).map((slug) => (
                            <BlogProductCard key={slug} slug={slug} onTrack={onTrack} compact />
                        ))}
                    </div>
                    <Link to="/shop" className="mt-3 inline-flex items-center gap-2 mono text-[10px] tracking-[0.16em] text-gold"
                        data-testid="sidebar-shop-link">VIEW PRODUCTS <ArrowRight className="w-3.5 h-3.5" /></Link>
                </Box>
            )}

            {!!popular.length && (
                <Box title="Popular articles" testId="sidebar-popular">
                    <div className="space-y-3">
                        {popular.slice(0, 4).map((p) => (
                            <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`sidebar-popular-${p.slug}`}
                                className="flex gap-3 group">
                                <div className="w-14 h-14 shrink-0 bg-bg0 overflow-hidden">
                                    {p.featured_image?.url && (
                                        <img src={p.featured_image.url} alt={p.featured_image.alt || p.title}
                                            loading="lazy" className="w-full h-full object-cover" />
                                    )}
                                </div>
                                <div className="min-w-0">
                                    <div className="text-sm leading-snug group-hover:text-gold transition-colors line-clamp-2">{p.title}</div>
                                    <div className="mono text-[9px] tracking-[0.14em] text-zinc-500 mt-1">{p.reading_minutes} MIN READ</div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </Box>
            )}

            {!!categories.length && (
                <Box title="Categories" testId="sidebar-categories">
                    <div className="flex flex-wrap gap-2">
                        {categories.filter((c) => c.post_count > 0).map((c) => (
                            <Link key={c.id} to={`/blog/category/${c.slug}`} data-testid={`sidebar-category-${c.slug}`}
                                className="border border-white/12 px-3 py-1.5 text-xs text-zinc-300 hover:border-gold hover:text-gold transition-colors">
                                {c.name} <span className="text-zinc-600">{c.post_count}</span>
                            </Link>
                        ))}
                    </div>
                </Box>
            )}

            {newsletter && (
                <Box title="Newsletter" testId="sidebar-newsletter">
                    <p className="text-sm text-zinc-400">New articles and offers, once a month. No spam.</p>
                    <form onSubmit={join} className="mt-3 flex">
                        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@email.com" data-testid="sidebar-newsletter-email"
                            className="flex-1 min-w-0 bg-bg0 border border-white/12 px-3 py-2 text-sm outline-none focus:border-gold" />
                        <button type="submit" data-testid="sidebar-newsletter-submit"
                            className="btn-gold px-3 mono text-[10px] tracking-[0.14em] font-bold">
                            <Mail className="w-3.5 h-3.5" />
                        </button>
                    </form>
                </Box>
            )}
        </div>
    );
}
